import { getTemplatePaginationStyles } from './Pagination.styles'
import {
  tryLoadDependencies,
  WebAwesomeComponent,
} from '@/components/webcomponents/Global/loadDependency'
import type { CallbackEvent } from './types/Pagination.type'
import type { PaginationProps } from './types/Pagination.type'
import { generatePaginationNumbers } from './utils'
import { dispatchAnalyticEvent } from '@/components/webcomponents/Global/analyticsEvents'

const prevButtonTemplate = document.createElement('template')
prevButtonTemplate.innerHTML = `
<wa-button exportparts="base:pagination-prev" @click="prevPage" appearance="outlined">
  <wa-icon name="chevron-left" label="previous"></wa-icon>
</wa-button>
`

const pageGapTemplate = document.createElement('template')
pageGapTemplate.innerHTML = `
  <wa-button exportparts="base:pagination-gap" disabled>...</wa-button>
`

const nextButtonTemplate = document.createElement('template')
nextButtonTemplate.innerHTML = `
<wa-button exportparts="base:pagination-next" @click="nextPage" appearance="outlined">
  <wa-icon name="chevron-right" label="next"></wa-icon>
</wa-button>
`

const pageEntryTemplate = document.createElement('template')
pageEntryTemplate.innerHTML = `
  <wa-button exportparts="base:pagination-page" variant="neutral" appearance="outlined filled" @click="goToPage(page)">{{ page }}</wa-button>
`

/* Simple variant templates */
const simplePrevButtonTemplate = document.createElement('template')
simplePrevButtonTemplate.innerHTML = `
<wa-button exportparts="base:pagination-prev" class="ts-pagination-simple__btn" appearance="outlined" variant="neutral">
  <wa-icon name="chevron-left" label="previous"></wa-icon>
  Prev
</wa-button>
`

const simpleNextButtonTemplate = document.createElement('template')
simpleNextButtonTemplate.innerHTML = `
<wa-button exportparts="base:pagination-next" class="ts-pagination-simple__btn" appearance="outlined" variant="neutral">
  Next
  <wa-icon name="chevron-right" label="next"></wa-icon>
</wa-button>
`

const t = document.createElement('template')
t.innerHTML = `
<div class="ts-split" part="pagination-container">
  <span class="ts-pagination__info">Showing page {{ currentPage }} of {{ totalPages }} pages</span>
  <wa-button-group orientation="horizontal" part="pagination-buttons">
  </wa-button-group>
</div>
`
const TemplatePagination = t

class Pagination extends HTMLElement {
  private readonly componentName = 'ts-pagination'
  private pageInfo: PaginationProps | null = null
  private _callback: ((e: CallbackEvent) => void) | null = null
  private static PageChangeEventName = 'ascend_pagination_pagechanged'

  constructor() {
    super()

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getTemplatePaginationStyles().content.cloneNode(true))
    shadow.appendChild(TemplatePagination.content.cloneNode(true))

    tryLoadDependencies(document, [WebAwesomeComponent.Button, WebAwesomeComponent.ButtonGroup])
  }

  /**
   * @deprecated Use event listeners. See docs for details.
   */
  set callback(callback: (e: CallbackEvent) => void) {
    document.AscendAwesome?.LogManager?.logWarning('callback is deprecated. See docs.')
    this._callback = callback
  }

  connectedCallback() {
    dispatchAnalyticEvent('ascend_pagination_loaded', 'load', this.componentName)
  }

  static get observedAttributes() {
    return ['data', 'variant']
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'data') {
      this.pageInfo = this.tryGetPagination()
      this.reload()
    }
    if (name === 'variant') {
      this.reload()
    }
  }

  private get isSimpleVariant(): boolean {
    return this.getAttribute('variant') === 'simple'
  }

  private tryGetPagination(): PaginationProps | null {
    const dataPagination = this.getAttribute('data')
    if (!dataPagination) {
      return null
    }
    try {
      return JSON.parse(dataPagination)
    } catch (e) {
      console.error('Invalid JSON:', e)
    }
    return null
  }

  private fireSelectedPageChange(page: number) {
    if (this.pageInfo) {
      this.pageInfo.currentPage = page
    }
    this.reload()

    if (this._callback) {
      this._callback({ actionType: 'page-selected', page })
    }

    this.dispatchEvent(
      new CustomEvent('page-selected', {
        detail: { actionType: 'page-selected', page },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private reload() {
    if (this.pageInfo == null || (this.pageInfo.autoHide && this.pageInfo.totalPages <= 1)) {
      this.shadowRoot?.querySelector('.ts-split')?.classList.add('hide')
      return
    }

    this.shadowRoot?.querySelector('.ts-split')?.classList.remove('hide')

    const paginationClone = this.shadowRoot
    if (!paginationClone) return

    const { currentPage, totalPages, pageSize, totalItems } = this.pageInfo

    // Update results info text
    const currentPageSpan = paginationClone.querySelector('.ts-pagination__info')
    if (currentPageSpan) {
      const startItem = (currentPage - 1) * pageSize + 1
      const endItem = Math.min(currentPage * pageSize, totalItems)
      currentPageSpan.textContent = `Showing ${startItem} to ${endItem} of ${totalItems} Results`
    }

    // Toggle simple variant class
    const container = paginationClone.querySelector('.ts-split')
    if (container) {
      container.classList.toggle('ts-split--simple', this.isSimpleVariant)
    }

    if (this.isSimpleVariant) {
      this.reloadSimple()
      return
    }

    const pages = generatePaginationNumbers(currentPage, totalPages)

    const pageList = paginationClone.querySelector('wa-button-group')
    if (pageList) {
      pageList.innerHTML = ''

      // prev button
      const prevButton = prevButtonTemplate.content.cloneNode(true) as HTMLElement
      const prevButtonElement = prevButton.querySelector('wa-button')
      if (prevButtonElement) {
        prevButtonElement.addEventListener('click', () => {
          if (this.pageInfo && this.pageInfo.currentPage > 1) {
            // Fire analytics event for table sorting
            dispatchAnalyticEvent(Pagination.PageChangeEventName, 'click', 'ts-pagination', {
              label: 'previous',
              context: this.pageInfo.analyticsContextKey ?? 'self',
            })
            this.fireSelectedPageChange(this.pageInfo.currentPage - 1)
          }
        })
      }
      pageList?.appendChild(prevButton)

      // pages
      for (let i = 0; i < pages.length; i++) {
        const pageEntry = pageEntryTemplate.content.cloneNode(true) as HTMLElement
        const pageButton = pageEntry.querySelector('wa-button')
        if (pageButton) {
          if (pages[i] === -1) {
            // gap
            pageButton.setAttribute('disabled', 'true')
            pageButton.textContent = '...'
            pageList.appendChild(pageEntry)
            continue
          }
          pageButton.textContent = pages[i].toString()

          if (pages[i] === this.pageInfo.currentPage) {
            pageButton.setAttribute('variant', 'brand')
          }

          pageButton.addEventListener('click', () => {
            dispatchAnalyticEvent(Pagination.PageChangeEventName, 'click', 'ts-pagination', {
              label: `${pageButton.textContent || 'unknown'}`,
              context: this.pageInfo?.analyticsContextKey ?? 'self',
            })
            this.fireSelectedPageChange(pages[i])
          })
        }
        pageList.appendChild(pageEntry)
      }

      // next button
      const nextButton = nextButtonTemplate.content.cloneNode(true) as HTMLElement
      const nextButtonElement = nextButton.querySelector('wa-button')
      if (nextButtonElement) {
        nextButtonElement.addEventListener('click', () => {
          if (this.pageInfo && this.pageInfo.currentPage < this.pageInfo.totalPages) {
            dispatchAnalyticEvent(Pagination.PageChangeEventName, 'click', 'ts-pagination', {
              label: 'next',
              context: this.pageInfo?.analyticsContextKey ?? 'self',
            })
            this.fireSelectedPageChange(this.pageInfo.currentPage + 1)
          }
        })
      }
      pageList?.appendChild(nextButton)
    }
  }

  private reloadSimple() {
    const paginationClone = this.shadowRoot
    if (!paginationClone) return

    const pageList = paginationClone.querySelector('wa-button-group')
    if (pageList) {
      pageList.innerHTML = ''

      // prev button with text
      const prevButton = simplePrevButtonTemplate.content.cloneNode(true) as HTMLElement
      const prevButtonElement = prevButton.querySelector('wa-button')
      if (prevButtonElement) {
        prevButtonElement.addEventListener('click', () => {
          if (this.pageInfo && this.pageInfo.currentPage > 1) {
            dispatchAnalyticEvent(Pagination.PageChangeEventName, 'click', 'ts-pagination', {
              label: 'previous',
              context: this.pageInfo.analyticsContextKey ?? 'self',
            })
            this.fireSelectedPageChange(this.pageInfo.currentPage - 1)
          }
        })
      }
      pageList.appendChild(prevButton)

      // next button with text
      const nextButton = simpleNextButtonTemplate.content.cloneNode(true) as HTMLElement
      const nextButtonElement = nextButton.querySelector('wa-button')
      if (nextButtonElement) {
        nextButtonElement.addEventListener('click', () => {
          if (this.pageInfo && this.pageInfo.currentPage < this.pageInfo.totalPages) {
            dispatchAnalyticEvent(Pagination.PageChangeEventName, 'click', 'ts-pagination', {
              label: 'next',
              context: this.pageInfo?.analyticsContextKey ?? 'self',
            })
            this.fireSelectedPageChange(this.pageInfo.currentPage + 1)
          }
        })
      }
      pageList.appendChild(nextButton)
    }
  }
}

if (!customElements.get('ts-pagination')) {
  customElements.define('ts-pagination', Pagination)
}
export { Pagination }
